import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactApexChart from 'react-apexcharts';
import Icon from './Icon';
import { isOfficerContextUser, isManagedSecretaryUser } from '../authStorage';
import { fetchSecretaryDashboard } from '../dashboardApi';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import './Dashboard.css';

/* ─── chart base configs ─────────────────────────────── */
const DONUT_COLORS  = ['#02c3a6', '#fb8c00', '#e53935'];
const RADIAL_COLORS = ['#02c3a6', '#1e88e5', '#fb8c00'];

const areaBase = {
  chart: { type: 'area', fontFamily: 'Cairo,sans-serif', toolbar: { show: false }, zoom: { enabled: false }, sparkline: { enabled: false } },
  colors: ['#02c3a6', '#e53935', '#fb8c00'],
  fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.02, stops: [0, 95] } },
  stroke: { curve: 'smooth', width: 2.5 },
  grid: { borderColor: '#f0f4fa', strokeDashArray: 4, padding: { left: 4, right: 4 } },
  xaxis: { labels: { style: { fontFamily: 'Cairo,sans-serif', fontSize: '11px', colors: '#90a4ae' } }, axisBorder: { show: false }, axisTicks: { show: false } },
  yaxis: { labels: { style: { fontFamily: 'Cairo,sans-serif', colors: '#90a4ae' } } },
  legend: { position: 'top', fontFamily: 'Cairo,sans-serif', fontSize: '12px' },
  tooltip: { style: { fontFamily: 'Cairo,sans-serif' } },
  dataLabels: { enabled: false },
};

const barBase = {
  chart: { type: 'bar', fontFamily: 'Cairo,sans-serif', toolbar: { show: false } },
  plotOptions: { bar: { borderRadius: 8, columnWidth: '52%', distributed: true } },
  colors: ['#02c3a6','#1e88e5','#00334a','#02c3a6','#fb8c00','#02c3a6','#00334a'],
  grid: { borderColor: '#f0f4fa', strokeDashArray: 4 },
  xaxis: { labels: { style: { fontFamily: 'Cairo,sans-serif', fontSize: '11px', colors: '#90a4ae' } }, axisBorder: { show: false }, axisTicks: { show: false } },
  yaxis: { labels: { style: { fontFamily: 'Cairo,sans-serif', colors: '#90a4ae' } } },
  legend: { show: false },
  tooltip: { style: { fontFamily: 'Cairo,sans-serif' } },
  dataLabels: { enabled: false },
};

/* ─── skeleton ───────────────────────────────────────── */
function Skeleton() {
  return (
    <div className="dash__skeleton">
      <div className="skel skel--banner" />
      <div className="skel--grid">
        {[0,1,2,3].map(i => <div key={i} className="skel skel--card" />)}
      </div>
      <div className="skel--charts-row">
        <div className="skel skel--chart" />
        <div className="skel skel--chart" />
      </div>
      <div className="skel skel--table" />
    </div>
  );
}

/* ─── quick action button ────────────────────────────── */
function QuickBtn({ emoji, label, onClick }) {
  return (
    <button className="dash__quick-btn" onClick={onClick}>
      <span className="dash__quick-icon">{emoji}</span>
      {label}
    </button>
  );
}

/* ─── stat card ─────────────────────────────────────── */
function StatCard({ variant, icon, iconColor, value, label, sub, trend, pct, onClick }) {
  return (
    <div className={`stat-card stat-card--${variant}`} onClick={onClick}>
      <div className="stat-card__top">
        <div className="stat-card__icon-wrap">
          <Icon name={icon} size={24} color={iconColor} />
        </div>
        {trend && (
          <span className={`stat-card__trend stat-card__trend--${trend}`}>
            {trend === 'up' ? '▲' : '▼'} {pct}
          </span>
        )}
      </div>
      <div className="stat-card__value">{value}</div>
      <div className="stat-card__label">{label}</div>
      <div className="stat-card__sub">{sub}</div>
      {pct !== undefined && (
        <div className="stat-card__progress">
          <div className="stat-card__progress-fill" style={{ width: `${Math.min(parseInt(pct) || 0, 100)}%` }} />
        </div>
      )}
    </div>
  );
}

/* ─── main component ─────────────────────────────────── */
export default function Dashboard() {
  const { language } = useLanguage();
  const t = useT();
  const isOfficer = isOfficerContextUser();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  /* date locale per language */
  const today = useMemo(() => {
    const d = new Date();
    const locale = language === 'ar' || language === 'ur' ? 'ar-SA-u-ca-gregory'
      : language === 'zh' ? 'zh-CN' : language === 'fr' ? 'fr-FR' : 'en-US';
    return {
      day:  d.toLocaleDateString(locale, { weekday: 'long' }),
      date: d.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' }),
    };
  }, [language]);

  useEffect(() => {
    // SecretaryAgent / Officer sub-accounts don't have access to /api/secretary/dashboard
    if (isManagedSecretaryUser()) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      setLoading(true); setLoadError('');
      try {
        const payload = await fetchSecretaryDashboard();
        if (!cancelled) setData(payload);
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : t('common.errorLoad'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const kpis = data?.kpis ?? {
    branchesTotal: 0, branchesActive: 0,
    boardMembersTotal: 0, boardMembersActive: 0,
    shareholdersTotal: 0,
    decisionsTotal: 0, decisionsPending: 0, decisionsApproved: 0, decisionsRejected: 0,
  };

  /* charts */
  const donut = useMemo(() => ({
    options: {
      chart: { type: 'donut', fontFamily: 'Cairo,sans-serif' },
      labels: [t('dashboard.donutApproved'), t('dashboard.donutPending'), t('dashboard.donutRejected')],
      colors: DONUT_COLORS,
      legend: { position: 'bottom', fontFamily: 'Cairo,sans-serif', fontSize: '13px' },
      plotOptions: {
        pie: { donut: { size: '68%', labels: {
          show: true,
          total: {
            show: true,
            label: t('dashboard.donutTotal'),
            fontFamily: 'Cairo,sans-serif', fontSize: '12px', color: '#546e7a',
            formatter: w => w.globals.seriesTotals.reduce((a,b) => a+b, 0),
          },
        }}},
      },
      dataLabels: { style: { fontFamily: 'Cairo,sans-serif' } },
      tooltip: { style: { fontFamily: 'Cairo,sans-serif' } },
      stroke: { width: 2 },
    },
    series: data?.decisionsDistribution ?? [0, 0, 0],
  }), [data, t]);

  const areaChart = useMemo(() => ({
    options: {
      ...areaBase,
      legend: { ...areaBase.legend, horizontalAlign: language === 'ar' || language === 'ur' ? 'right' : 'left' },
      xaxis: { ...areaBase.xaxis, categories: data?.votesTrend?.categories ?? [] },
    },
    series: [
      { name: t('dashboard.vote.approve'), data: data?.votesTrend?.approve  ?? [] },
      { name: t('dashboard.vote.reject'),  data: data?.votesTrend?.reject   ?? [] },
      { name: t('dashboard.vote.abstain'), data: data?.votesTrend?.abstain  ?? [] },
    ],
  }), [data, language, t]);

  const barChart = useMemo(() => ({
    options: {
      ...barBase,
      xaxis: { ...barBase.xaxis, categories: data?.branchesBoard?.categories ?? [] },
    },
    series: [{ name: t('dashboard.vote.members'), data: data?.branchesBoard?.values ?? [] }],
  }), [data, t]);

  const radial = useMemo(() => ({
    options: {
      chart: { type: 'radialBar', fontFamily: 'Cairo,sans-serif' },
      labels: data?.shareholderTypes?.labels ?? [],
      colors: RADIAL_COLORS,
      plotOptions: {
        radialBar: {
          dataLabels: {
            name: { fontFamily: 'Cairo,sans-serif', fontSize: '13px' },
            value: { fontFamily: 'Cairo,sans-serif', fontSize: '14px', fontWeight: '700' },
            total: {
              show: true,
              label: t('dashboard.radialTotal'),
              fontFamily: 'Cairo,sans-serif', fontSize: '12px', color: '#546e7a',
              formatter: () => '100%',
            },
          },
          track: { background: '#f0f4fa' },
          hollow: { size: '40%' },
        },
      },
      legend: { show: true, position: 'bottom', fontFamily: 'Cairo,sans-serif', fontSize: '12px' },
      tooltip: { style: { fontFamily: 'Cairo,sans-serif' } },
    },
    series: data?.shareholderTypes?.series ?? [],
  }), [data, t]);

  const statusMap = useMemo(() => ({
    approved: { label: t('common.approved'),       cls: 'status-badge--approved', icon: 'check-circle' },
    pending:  { label: t('dashboard.donutPending'), cls: 'status-badge--pending',  icon: 'clock' },
    rejected: { label: t('common.rejected'),       cls: 'status-badge--rejected', icon: 'x-circle' },
  }), [t]);

  const recentRows = data?.recentDecisions ?? [];

  if (loading) return <Skeleton />;

  return (
    <div className="dash">
      {loadError && <div className="dash__error">{loadError}</div>}

      {/* ── Welcome banner ── */}
      <div className="dash__welcome">
        <div className="dash__welcome-left">
          <div className="dash__welcome-avatar">
            {isOfficer ? '📋' : '👔'}
          </div>
          <div className="dash__welcome-text">
            <h2>
              {isOfficer ? t('dashboard.welcomeOfficer') : t('dashboard.welcomeCeo')}
            </h2>
            <p>
              {isOfficer ? t('dashboard.officerSub') : t('dashboard.ceoSub')}
            </p>
          </div>
        </div>

        <div className="dash__welcome-right">
          <div className="dash__welcome-date">
            <strong>{today.day}</strong>
            {today.date}
          </div>
          <div className="dash__welcome-actions">
            <button className="dash__welcome-btn dash__welcome-btn--primary" onClick={() => navigate('/app/decisions/new')}>
              <Icon name="plus" size={15} color="#00334a" />
              {t('dashboard.newDecision')}
            </button>
            <button className="dash__welcome-btn dash__welcome-btn--outline" onClick={() => navigate('/app/meetings/create')}>
              <Icon name="calendar" size={15} color="#fff" />
              {t('dashboard.newMeeting')}
            </button>
          </div>
        </div>
      </div>

      {/* ── Quick actions ── */}
      <div className="dash__quick">
        <QuickBtn emoji="🏢" label={t('nav.branches')}      onClick={() => navigate('/app/branches')} />
        <QuickBtn emoji="👥" label={t('nav.board')}         onClick={() => navigate('/app/board')} />
        <QuickBtn emoji="📊" label={t('common.decisions')}  onClick={() => navigate('/app/decisions')} />
        <QuickBtn emoji="🗓️" label={t('common.meetings')}   onClick={() => navigate('/app/meetings')} />
        <QuickBtn emoji="👤" label={t('nav.shareholders')}  onClick={() => navigate('/app/shareholders')} />
      </div>

      {/* ── KPI cards ── */}
      <div className="dash__stats">
        <StatCard
          variant="blue" icon="branch" iconColor="#02c3a6"
          value={kpis.branchesTotal}
          label={t('dashboard.registeredBranches')}
          sub={`${kpis.branchesActive} ${t('dashboard.activeLabel')}`}
          trend="up" pct="72"
          onClick={() => navigate('/app/branches')}
        />
        <StatCard
          variant="green" icon="board" iconColor="#1e88e5"
          value={kpis.boardMembersTotal}
          label={t('dashboard.boardMembersLabel')}
          sub={`${kpis.boardMembersActive} ${t('dashboard.activeLabel')}`}
          trend="up" pct="58"
          onClick={() => navigate('/app/board')}
        />
        <StatCard
          variant="orange" icon="money" iconColor="#fb8c00"
          value={kpis.shareholdersTotal}
          label={t('nav.shareholders')}
          sub={t('dashboard.acrossAllBranches')}
          trend="up" pct="45"
          onClick={() => navigate('/app/shareholders')}
        />
        <StatCard
          variant="purple" icon="decisions" iconColor="#8e24aa"
          value={kpis.decisionsTotal}
          label={t('dashboard.totalDecisionsLabel')}
          sub={`${kpis.decisionsPending} ${t('dashboard.pendingLabel')} · ${kpis.decisionsApproved} ${t('dashboard.approvedLabel')}`}
          trend={kpis.decisionsRejected > 0 ? 'down' : 'up'} pct="64"
          onClick={() => navigate('/app/decisions')}
        />
      </div>

      {/* ── Charts row 1 ── */}
      <div className="dash__charts">
        <div className="chart-card">
          <div className="chart-card__header">
            <span className="chart-card__title">
              <Icon name="chart" size={15} color="#02c3a6" />
              {t('dashboard.monthlyVotingTrend')}
            </span>
            <span className="chart-card__badge">{t('dashboard.last8Months')}</span>
          </div>
          <p className="chart-card__subtitle">{t('dashboard.trackVotes')}</p>
          <ReactApexChart options={areaChart.options} series={areaChart.series} type="area" height={245} />
        </div>

        <div className="chart-card">
          <div className="chart-card__header">
            <span className="chart-card__title">
              <Icon name="pie-chart" size={15} color="#02c3a6" />
              {t('dashboard.decisionsDistTitle')}
            </span>
            <span className="chart-card__badge">{kpis.decisionsTotal} {t('dashboard.decisionsUnit')}</span>
          </div>
          <p className="chart-card__subtitle">{t('dashboard.decisionsBreakdown')}</p>
          <ReactApexChart options={donut.options} series={donut.series} type="donut" height={245} />
        </div>
      </div>

      {/* ── Charts row 2 ── */}
      <div className="dash__charts dash__charts--bottom">
        <div className="chart-card">
          <div className="chart-card__header">
            <span className="chart-card__title">
              <Icon name="board" size={15} color="#02c3a6" />
              {t('dashboard.boardMembersPerBranch')}
            </span>
            <span className="chart-card__badge">
              {data?.branchesBoard?.categories?.length ?? 0} {t('dashboard.branchUnit')}
            </span>
          </div>
          <p className="chart-card__subtitle">{t('dashboard.memberCountComparison')}</p>
          <ReactApexChart options={barChart.options} series={barChart.series} type="bar" height={245} />
        </div>

        <div className="chart-card">
          <div className="chart-card__header">
            <span className="chart-card__title">
              <Icon name="money" size={15} color="#02c3a6" />
              {t('dashboard.shareholderDistTitle')}
            </span>
            <span className="chart-card__badge">
              {data?.shareholderTypes?.labels?.length ?? 0} {t('common.filter')}
            </span>
          </div>
          <p className="chart-card__subtitle">{t('dashboard.ownershipRatio')}</p>
          <ReactApexChart options={radial.options} series={radial.series} type="radialBar" height={245} />
        </div>
      </div>

      {/* ── Recent decisions ── */}
      <div className="dash__recent">
        <div className="dash__recent-header">
          <span className="dash__recent-title">
            <Icon name="clock" size={15} color="#02c3a6" />
            {t('dashboard.recentDecisions')}
          </span>
          <span className="dash__recent-link" onClick={() => navigate('/app/decisions')}>
            {t('dashboard.viewAll')}
            <Icon name="chevron-left" size={12} />
          </span>
        </div>

        <table className="dash__table">
          <thead>
            <tr>
              <th>#</th>
              <th>{t('dashboard.decisionCol')}</th>
              <th>{t('common.company')}</th>
              <th>{t('common.date')}</th>
              <th>{t('dashboard.votesCol')}</th>
              <th>{t('dashboard.statusCol')}</th>
            </tr>
          </thead>
          <tbody>
            {recentRows.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: '28px', color: '#90a4ae' }}>
                  {t('dashboard.noRecentDecisions')}
                </td>
              </tr>
            ) : recentRows.map((row) => {
              const s = statusMap[row.status] ?? statusMap.pending;
              return (
                <tr key={row.id}>
                  <td><span className="dash__table-id">{row.id}</span></td>
                  <td><span className="dash__table-title">{row.title}</span></td>
                  <td><span className="dash__table-branch">🏢 {row.branch}</span></td>
                  <td><span className="dash__table-votes">{row.date}</span></td>
                  <td><span className="dash__table-votes">{row.votes}</span></td>
                  <td>
                    <span className={`status-badge ${s.cls}`}>
                      <Icon name={s.icon} size={11} />
                      {s.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
